import { useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { DeliveryZone } from '@/types/index';
import { useToast } from '@/hooks/use-toast';
import { useCachedData, useCacheInvalidation } from './useCache';
import { CacheKeys, UserContext } from '@/stores/cacheKeys';
import { TTL_CONFIG } from '@/stores/cacheConfig';

// Hook for public access to delivery zones (customers)
export const usePublicDeliveryZones = (branchId: string | null) => {
  const { data: deliveryZones, isLoading, error } = useQuery<DeliveryZone[]>({
    queryKey: ['publicDeliveryZones', branchId],
    queryFn: async () => {
      if (!branchId) return [];
      
      const { data, error } = await supabase
        .from('delivery_zones')
        .select('*')
        .eq('branch_id', branchId)
        .eq('is_active', true)
        .order('delivery_fee', { ascending: true });

      if (error) throw error;
      return data || [];
    },
    enabled: !!branchId,
  });

  return {
    deliveryZones: deliveryZones || [],
    loading: isLoading,
    error,
  };
};

// Hook for restaurant owners and staff to manage delivery zones
export const useDeliveryZones = (branchId: string | null) => {
  const { user, isStaff, staffSession } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { invalidateCache } = useCacheInvalidation();

  // Create user context for caching (memoized to prevent dependency issues)
  const userContext: UserContext | null = useMemo(() => {
    return user || staffSession ? {
      userId: user?.id || staffSession?.id || '',
      restaurantId: staffSession?.restaurant_id || '',
      isStaff: isStaff,
      allowedBranches: staffSession?.allowed_branches || []
    } : null;
  }, [user, isStaff, staffSession]);

  // Generate cache key
  const cacheKey = userContext && branchId ? CacheKeys.deliveryZones(userContext, branchId) : '';

  // Define the fetcher function
  const fetchDeliveryZones = useCallback(async (): Promise<DeliveryZone[]> => {
    // Check if user is authenticated (either owner or staff)
    if (!branchId || (!user && !isStaff) || !userContext) return [];

    // For staff users, check if they have access to the branch
    if (isStaff && staffSession) {
      if (staffSession.allowed_branches && staffSession.allowed_branches.length > 0) {
        if (!staffSession.allowed_branches.includes(branchId)) {
          throw new Error('Access denied to this branch');
        }
      }
    }
    
    const { data, error } = await supabase
      .from('delivery_zones')
      .select('*')
      .eq('branch_id', branchId)
      .order('delivery_fee', { ascending: true });

    if (error) throw error;
    return data || [];
  }, [branchId, user, isStaff, staffSession, userContext]);

  // Use cached data hook
  const {
    data: cachedDeliveryZones,
    loading: cacheLoading,
    error: cacheError,
    isFromCache,
    refetch: refetchCache,
    invalidate: invalidateCachedData
  } = useCachedData<DeliveryZone[]>(
    cacheKey,
    fetchDeliveryZones,
    {
      enabled: !!branchId && !!userContext,
      ttl: TTL_CONFIG.deliveryZones,
      fallbackToCache: true,
      backgroundRefresh: false
    }
  );

  // Also use React Query for mutation support
  const queryKey = ['deliveryZones', branchId];
  const { data: queryDeliveryZones, isLoading: queryLoading, error: queryError, refetch: refetchQuery } = useQuery<DeliveryZone[]>({
    queryKey,
    queryFn: fetchDeliveryZones,
    enabled: !!branchId && (!!user || !!isStaff) && !isFromCache,
    staleTime: TTL_CONFIG.deliveryZones,
  });

  const createDeliveryZoneMutation = useMutation({
    mutationFn: async (newZone: Omit<DeliveryZone, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('delivery_zones')
        .insert(newZone)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey });
      await invalidateCache(['deliveryZones'], branchId || undefined);
      toast({
        title: "Success",
        description: "Delivery zone created successfully!",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to create delivery zone: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  const updateDeliveryZoneMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<DeliveryZone> }) => {
      const { data, error } = await supabase
        .from('delivery_zones')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey });
      await invalidateCache(['deliveryZones'], branchId || undefined);
      toast({
        title: "Success",
        description: "Delivery zone updated successfully!",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to update delivery zone: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  const deleteDeliveryZoneMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('delivery_zones')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey });
      await invalidateCache(['deliveryZones'], branchId || undefined);
      toast({
        title: "Success",
        description: "Delivery zone deleted successfully!",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to delete delivery zone: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  // Determine which data to use (prefer cache, fallback to query)
  const deliveryZones = cachedDeliveryZones || queryDeliveryZones || [];
  const loading = cacheLoading || queryLoading;
  const error = cacheError || queryError;

  // Combined refetch function
  const refetch = useCallback(async () => {
    await Promise.all([
      refetchCache(),
      refetchQuery()
    ]);
  }, [refetchCache, refetchQuery]);

  return {
    deliveryZones,
    loading,
    error,
    isFromCache,
    refetch,
    invalidate: invalidateCachedData,
    createDeliveryZone: createDeliveryZoneMutation.mutate,
    updateDeliveryZone: updateDeliveryZoneMutation.mutate,
    deleteDeliveryZone: deleteDeliveryZoneMutation.mutate,
    isCreating: createDeliveryZoneMutation.isPending,
    isUpdating: updateDeliveryZoneMutation.isPending,
    isDeleting: deleteDeliveryZoneMutation.isPending,
  };
}; 